import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './VisibilityMetricsView.css';

interface VisibilityMetric {
  dealer_id: string;
  dealer_name: string;
  visibility_status: 'zero' | 'critical' | 'low' | 'fair' | 'good';
  total_impressions: number;
  last_analyzed: string;
  impressions_10_mile: number;
  impressions_20_mile: number;
  impressions_30_mile: number;
  channels: {
    display?: number;
    radio?: number;
    tv?: number;
    ctv_ott?: number;
  };
  channel_visibility?: {
    [channel: string]: {
      impressions: number;
      status: 'zero' | 'critical' | 'low' | 'fair' | 'good';
    };
  };
}

interface VisibilitySummary {
  zero_count: number;
  critical_count: number;
  low_count: number;
  fair_count: number;
  good_count: number;
  total_dealers: number;
}

const VisibilityMetricsView: React.FC = () => {
  const [metrics, setMetrics] = useState<VisibilityMetric[]>([]);
  const [summary, setSummary] = useState<VisibilitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterChannel, setFilterChannel] = useState<string>('all'); // Phase 2.2.3
  const [searchTerm, setSearchTerm] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalcMessage, setRecalcMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchVisibilityMetrics();
  }, []);

  const fetchVisibilityMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminAPI.getVisibilityMetrics();
      setMetrics(data.metrics || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      console.error('Visibility metrics error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load visibility metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    setRecalcMessage(null);
    try {
      const result = await adminAPI.recalculateVisibility(30);
      setRecalcMessage(result.message || 'Recalculation started — refresh in 2-3 minutes to see updated metrics.');
    } catch (err: any) {
      setRecalcMessage(err.response?.data?.error || 'Recalculation failed.');
    } finally {
      setRecalculating(false);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'zero': return 'status-badge status-zero';
      case 'critical': return 'status-badge status-critical';
      case 'low': return 'status-badge status-low';
      case 'fair': return 'status-badge status-fair';
      case 'good': return 'status-badge status-good';
      default: return 'status-badge';
    }
  };

  const getStatusLabel = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Phase 2.2.3: Get the current display status and impressions based on active filter
  const getDisplayStatus = (metric: VisibilityMetric) => {
    if (filterChannel !== 'all' && metric.channel_visibility) {
      const channelData = metric.channel_visibility[filterChannel];
      return {
        status: channelData?.status || 'zero',
        impressions: channelData?.impressions || 0
      };
    }
    return {
      status: metric.visibility_status,
      impressions: metric.total_impressions
    };
  };

  const filteredMetrics = metrics.filter(metric => {
    // Phase 2.2.3: Channel filter logic
    let matchesChannel = true;
    if (filterChannel !== 'all' && metric.channel_visibility) {
      const channelData = metric.channel_visibility[filterChannel];
      if (channelData) {
        // Filter by channel-specific status
        matchesChannel = filterStatus === 'all' || channelData.status === filterStatus;
      } else {
        matchesChannel = false;
      }
    } else {
      // Legacy filter (overall status)
      matchesChannel = filterStatus === 'all' || metric.visibility_status === filterStatus;
    }

    const matchesSearch = searchTerm === '' ||
      metric.dealer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      metric.dealer_id.includes(searchTerm);

    return matchesChannel && matchesSearch;
  });

  if (loading) {
    return (
      <div className="visibility-metrics-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading visibility metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visibility-metrics-view">
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button onClick={fetchVisibilityMetrics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="visibility-metrics-view">
      <div className="view-header">
        <div className="view-header-top">
          <h2>Dealer Visibility Metrics</h2>
          <button
            className="refresh-visibility-btn"
            onClick={handleRecalculate}
            disabled={recalculating}
          >
            {recalculating ? 'Refreshing...' : 'Refresh Visibility Data'}
          </button>
        </div>
        <p className="view-description">
          Monitor advertising visibility for all dealers across their coverage areas
        </p>
        {recalcMessage && (
          <p className="recalc-message">{recalcMessage}</p>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-grid">
          <div className="summary-card summary-total">
            <div className="summary-value">{summary.total_dealers}</div>
            <div className="summary-label">Total Dealers</div>
          </div>
          <div className="summary-card summary-good">
            <div className="summary-value">{summary.good_count}</div>
            <div className="summary-label">Good Visibility</div>
            <div className="summary-percent">
              {Math.round((summary.good_count / summary.total_dealers) * 100)}%
            </div>
          </div>
          <div className="summary-card summary-fair">
            <div className="summary-value">{summary.fair_count}</div>
            <div className="summary-label">Fair Visibility</div>
            <div className="summary-percent">
              {Math.round((summary.fair_count / summary.total_dealers) * 100)}%
            </div>
          </div>
          <div className="summary-card summary-low">
            <div className="summary-value">{summary.low_count}</div>
            <div className="summary-label">Low Visibility</div>
            <div className="summary-percent">
              {Math.round((summary.low_count / summary.total_dealers) * 100)}%
            </div>
          </div>
          <div className="summary-card summary-critical">
            <div className="summary-value">{summary.critical_count}</div>
            <div className="summary-label">Critical</div>
            <div className="summary-percent">
              {Math.round((summary.critical_count / summary.total_dealers) * 100)}%
            </div>
          </div>
          <div className="summary-card summary-zero">
            <div className="summary-value">{summary.zero_count}</div>
            <div className="summary-label">Zero Visibility</div>
            <div className="summary-percent">
              {Math.round((summary.zero_count / summary.total_dealers) * 100)}%
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Filter by Channel:</label>
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Channels (Overall)</option>
            <option value="Display">Display</option>
            <option value="Radio">Radio</option>
            <option value="TV">TV</option>
            <option value="YouTube">YouTube</option>
            <option value="Search">Search</option>
            <option value="DOOH">DOOH</option>
            <option value="CTV/OTT">CTV/OTT</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="zero">Zero Visibility</option>
            <option value="critical">Critical</option>
            <option value="low">Low</option>
            <option value="fair">Fair</option>
            <option value="good">Good</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Dealer name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="results-count">
          Showing {filteredMetrics.length} of {metrics.length} dealers
          {filterChannel !== 'all' && <span className="filter-badge"> ({filterChannel})</span>}
        </div>
      </div>

      {/* Metrics Table */}
      <div className="metrics-table-container">
        <table className="metrics-table">
          <thead>
            <tr>
              <th>Dealer ID</th>
              <th>Dealer Name</th>
              <th>Status</th>
              <th>Total Impressions</th>
              <th>10-Mile</th>
              <th>20-Mile</th>
              <th>30-Mile</th>
              <th>Display</th>
              <th>Radio</th>
              <th>TV</th>
              <th>CTV/OTT</th>
              <th>Last Analyzed</th>
            </tr>
          </thead>
          <tbody>
            {filteredMetrics.length === 0 ? (
              <tr>
                <td colSpan={12} className="no-results">
                  No dealers found matching your filters
                </td>
              </tr>
            ) : (
              filteredMetrics.map((metric) => {
                const displayStatus = getDisplayStatus(metric);
                return (
                  <tr key={metric.dealer_id}>
                    <td className="dealer-id">{metric.dealer_id}</td>
                    <td className="dealer-name">{metric.dealer_name}</td>
                    <td>
                      <span className={getStatusBadgeClass(displayStatus.status)}>
                        {getStatusLabel(displayStatus.status)}
                        {filterChannel !== 'all' && <span className="channel-indicator"> ({filterChannel})</span>}
                      </span>
                    </td>
                    <td className="number-cell">
                      {filterChannel !== 'all' ? formatNumber(displayStatus.impressions) : formatNumber(metric.total_impressions)}
                    </td>
                    <td className="number-cell">{formatNumber(metric.impressions_10_mile)}</td>
                    <td className="number-cell">{formatNumber(metric.impressions_20_mile)}</td>
                    <td className="number-cell">{formatNumber(metric.impressions_30_mile)}</td>
                    <td className="number-cell">{formatNumber(metric.channels.display || 0)}</td>
                    <td className="number-cell">{formatNumber(metric.channels.radio || 0)}</td>
                    <td className="number-cell">{formatNumber(metric.channels.tv || 0)}</td>
                    <td className="number-cell">{formatNumber(metric.channels.ctv_ott || 0)}</td>
                    <td className="date-cell">{formatDate(metric.last_analyzed)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VisibilityMetricsView;
